import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Library, Upload, Trash2, Loader2, AlertCircle, Play, Pause, Plus, Music, Volume2, Mic } from 'lucide-react'
import { libraryApi } from '../api/client'

export interface LibraryAsset {
  slug: string
  name: string
  kind: 'sfx' | 'music' | 'voice'
  tags: string[]
  seconds: number | null
  status: 'pending' | 'ready' | 'failed'
  created_at: string
}

interface LibraryPanelProps {
  /** Drop a sound onto the timeline's audio lane. */
  onAddToTimeline?: (clip: { url: string; text: string; duration?: number; kind: string }) => void
}

const KIND_ICON = { sfx: Volume2, music: Music, voice: Mic } as const
const MAX_MB = 50

/**
 * The user's OWN sounds — the ones the assistant means when they say "my airhorn".
 *
 * This exists because the assistant could already reference a personal library that
 * had no way in: the API landed before any surface for it, so a creator could be
 * told "I added your stinger" about a sound they had no means of adding. The panel
 * is the missing half.
 */
export function LibraryPanel({ onAddToTimeline }: LibraryPanelProps = {}) {
  const [assets, setAssets] = useState<LibraryAsset[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [kind, setKind] = useState<'sfx' | 'music' | 'voice'>('sfx')
  const [playing, setPlaying] = useState<string | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const fileRef = useRef<HTMLInputElement | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      const data = await libraryApi.list()
      setAssets(data.assets || [])
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Could not load your library')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])
  useEffect(() => () => { audioRef.current?.pause() }, [])

  const upload = async (files: FileList | File[]) => {
    const list = Array.from(files)
    if (!list.length) return
    setUploading(true); setError(null)
    for (const f of list) {
      // Checked here as well as server-side: a creator who just waited through a
      // 200MB upload only to be refused has been failed twice.
      if (f.size > MAX_MB * 1024 * 1024) {
        setError(`${f.name} is ${(f.size / 1e6).toFixed(0)}MB — the limit is ${MAX_MB}MB`)
        continue
      }
      try {
        await libraryApi.upload(f, kind)
      } catch (e: any) {
        setError(e?.response?.data?.error || `Could not add ${f.name}`)
      }
    }
    setUploading(false)
    load()
  }

  const remove = async (slug: string) => {
    // Optimistic: the row is gone from the server or it is not, and re-listing
    // afterwards settles it either way.
    setAssets((a) => a.filter((x) => x.slug !== slug))
    try { await libraryApi.remove(slug) } catch { load() }
  }

  const preview = (a: LibraryAsset) => {
    if (playing === a.slug) { audioRef.current?.pause(); setPlaying(null); return }
    audioRef.current?.pause()
    const el = new Audio(libraryApi.streamUrl(a.slug))
    el.onended = () => setPlaying(null)
    el.onerror = () => { setError(`Could not play ${a.name}`); setPlaying(null) }
    audioRef.current = el
    el.play().then(() => setPlaying(a.slug)).catch(() => setPlaying(null))
  }

  const ready = assets.filter((a) => a.status === 'ready')
  const pending = assets.filter((a) => a.status !== 'ready')

  return (
    <div className="space-y-3">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); upload(e.dataTransfer.files) }}
        onClick={() => fileRef.current?.click()}
        className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
          dragOver ? 'border-emerald-400 bg-emerald-400/10' : 'border-gray-600 hover:border-gray-500'
        }`}
      >
        <input ref={fileRef} type="file" accept="audio/*" multiple className="hidden"
               onChange={(e) => e.target.files && upload(e.target.files)} />
        {uploading
          ? <div className="flex items-center justify-center gap-2 text-sm text-gray-300"><Loader2 className="w-4 h-4 animate-spin" /> Adding…</div>
          : <div className="text-sm text-gray-300"><Upload className="w-4 h-4 inline mr-1" /> Drop audio here, or click to choose</div>}
        <div className="text-[11px] text-gray-500 mt-1">wav · mp3 · m4a · ogg · flac — up to {MAX_MB}MB</div>
      </div>

      <div className="flex gap-1">
        {(['sfx', 'music', 'voice'] as const).map((k) => (
          <button key={k} onClick={() => setKind(k)}
            className={`px-2 py-1 rounded text-xs capitalize ${kind === k ? 'bg-emerald-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}>
            {k}
          </button>
        ))}
        <span className="text-[11px] text-gray-500 self-center ml-1">what you add is filed as</span>
      </div>

      {error && (
        <div className="flex items-start gap-2 text-xs text-red-300 bg-red-900/30 rounded p-2">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" /><span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-gray-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading your library…</div>
      ) : !assets.length ? (
        <div className="text-sm text-gray-400 text-center py-6">
          <Library className="w-6 h-6 mx-auto mb-2 opacity-50" />
          Nothing here yet. Add your stingers, beds and voice tags —
          then just tell the assistant &ldquo;use my airhorn&rdquo;.
        </div>
      ) : (
        <div className="space-y-1 max-h-80 overflow-y-auto">
          {ready.map((a) => {
            const Icon = KIND_ICON[a.kind] || Volume2
            return (
              <div key={a.slug} className="flex items-center gap-2 bg-gray-800/60 rounded px-2 py-1.5 group">
                <button onClick={() => preview(a)} className="text-gray-300 hover:text-white shrink-0" title="Preview">
                  {playing === a.slug ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                </button>
                <Icon className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-gray-200 truncate">{a.name}</div>
                  {/* The slug is what the assistant answers to, so it is shown, not hidden. */}
                  <div className="text-[10px] text-gray-500 truncate">
                    {a.slug}{a.seconds ? ` · ${a.seconds.toFixed(1)}s` : ''}{a.tags?.length ? ` · ${a.tags.join(', ')}` : ''}
                  </div>
                </div>
                {onAddToTimeline && (
                  <button title="Add to timeline"
                    onClick={() => onAddToTimeline({ url: libraryApi.streamUrl(a.slug), text: a.name, duration: a.seconds || undefined, kind: a.kind })}
                    className="text-gray-400 hover:text-emerald-400 shrink-0 opacity-0 group-hover:opacity-100">
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                )}
                <button title="Remove" onClick={() => remove(a.slug)}
                  className="text-gray-500 hover:text-red-400 shrink-0 opacity-0 group-hover:opacity-100">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            )
          })}
          {pending.map((a) => (
            <div key={a.slug} className="flex items-center gap-2 px-2 py-1.5 text-xs text-gray-500">
              <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
              <span className="truncate">{a.name}</span>
              <span className="ml-auto">{a.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
